
import { useState, useCallback } from 'react';

interface PasswordValidationResult {
  isValid: boolean;
  strength: number;
  feedback: string;
}

interface PasswordCriteria {
  lengthValid: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

/**
 * Custom hook for password validation and strength calculation
 * 
 * Validates passwords against 5 criteria:
 * - Minimum length (8 characters)
 * - Contains uppercase letter
 * - Contains lowercase letter
 * - Contains number
 * - Contains special character
 * 
 * Returns functions to validate passwords and get visual feedback
 */
export const usePasswordValidation = () => {
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState<string>('');

  /**
   * Evaluates a password against all security criteria
   * @returns Object containing validation state for each criterion
   */
  const evaluatePasswordCriteria = useCallback((password: string): PasswordCriteria => {
    const minLength = 8;
    
    return {
      lengthValid: password.length >= minLength,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password)
    };
  }, []);

  /**
   * Calculate password strength based on criteria met
   * Each criterion is worth 20 points (total 100 possible)
   */
  const calculateStrength = useCallback((criteria: PasswordCriteria): number => {
    let strength = 0;
    
    if (criteria.lengthValid) strength += 20;
    if (criteria.hasUppercase) strength += 20;
    if (criteria.hasLowercase) strength += 20;
    if (criteria.hasNumber) strength += 20;
    if (criteria.hasSpecial) strength += 20;
    
    return strength;
  }, []);

  /**
   * Get appropriate feedback message based on failed criteria
   */
  const getFeedbackMessage = useCallback((criteria: PasswordCriteria, strength: number): string => {
    if (!criteria.lengthValid) {
      return 'Password must be at least 8 characters long';
    } else if (!criteria.hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    } else if (!criteria.hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    } else if (!criteria.hasNumber) {
      return 'Password must contain at least one number';
    } else if (!criteria.hasSpecial) {
      return 'Password must contain at least one special character';
    }
    
    return strength < 100 
      ? `Password strength: ${getStrengthLabel(strength)}`
      : 'Strong password';
  }, []);

  /**
   * Validates a password and updates state
   * @returns Validation result object
   */
  const validatePassword = useCallback((password: string): PasswordValidationResult => {
    // Empty password check
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback('');
      return { isValid: false, strength: 0, feedback: '' };
    }
    
    // Evaluate all criteria at once
    const criteria = evaluatePasswordCriteria(password);
    const strength = calculateStrength(criteria);
    const feedback = getFeedbackMessage(criteria, strength);
    
    // Update state
    setPasswordStrength(strength);
    setPasswordFeedback(feedback);
    
    return { 
      isValid: strength === 100, 
      strength, 
      feedback 
    };
  }, [evaluatePasswordCriteria, calculateStrength, getFeedbackMessage]);
  
  /**
   * Maps numerical strength to descriptive label
   */
  const getStrengthLabel = useCallback((strength: number): string => {
    if (strength <= 20) return 'Very Weak';
    if (strength <= 40) return 'Weak';
    if (strength <= 60) return 'Medium';
    if (strength <= 80) return 'Good';
    return 'Strong';
  }, []);
  
  /**
   * Maps numerical strength to appropriate Tailwind color class
   */
  const getProgressColor = useCallback((strength: number): string => {
    if (strength <= 20) return 'bg-red-500';
    if (strength <= 40) return 'bg-orange-500';
    if (strength <= 60) return 'bg-yellow-500';
    if (strength <= 80) return 'bg-blue-500';
    return 'bg-green-500';
  }, []);

  return {
    passwordStrength,
    passwordFeedback,
    validatePassword,
    getStrengthLabel,
    getProgressColor
  };
};
